/**
 * Local Radar Sweep — „lokale Entdeckung" als kinetisches Radar.
 *
 * Ein zentrierter Radar-Disc mit 2–3 konzentrischen Ringen + feinem Grid und
 * einer kontinuierlich rotierenden Sweep-Keule (CSS @keyframes, ~3.6s Loop).
 * Rund um das Radar poppen Anbieter-/Kategorie-Pins (kleine runde Chips mit
 * Lucide-Icon + Mini-Label) gestaffelt ein, als würde der Sweep sie aufdecken.
 * In der Mitte pulsiert ein Dot + ein kleiner Live-Counter; darüber die
 * Headline (inputs.text), darunter die Subline (inputs.subtext), optional ein
 * Eyebrow (inputs.extras.eyebrow).
 *
 * Items-Format:  "Label | iconKey"
 *   z. B. "Restaurants | shop"
 *   (ohne iconKey → Fallback "pin")
 *
 * Inputs:
 *   - text          → Headline (Default „Lokale Anbieter in deiner Nähe")
 *   - subtext       → Subline (Default „Allyver scannt deine Region.")
 *   - extras.eyebrow → kleines Uppercase-Label (Default „ENTDECKEN")
 *   - accent        → Akzent-Token (Default Allyver-Grün/primary)
 *   - items         → bis zu 7 „Label | iconKey"
 *
 * Motion: Radar skaliert ein → Pins poppen gestaffelt (scale 0→1 + Glow) über
 * die ersten ~2.5s → Sweep rotiert dauerhaft → Center-Dot pulsiert.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "../visual/_mockHelpers.js";

export const localRadarSweep: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const headline = inputs.text ?? "Lokale Anbieter in deiner Nähe";
  const subtext = inputs.subtext ?? "Allyver scannt deine Region.";
  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "ENTDECKEN";

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Restaurants | shop",
        "Shops | store",
        "Taxis | taxi",
        "Hotels | hotel",
        "Events | event",
        "Services | service",
      ]
  ).slice(0, 7);

  const parsed = items.map((raw) => {
    const [label, iconRaw] = splitItem(raw);
    return {
      label: (label ?? "").trim(),
      icon: (iconRaw ?? "").trim() || "pin",
    };
  });

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  // Basis-Radargröße aus Format ableiten — füllt 9:16 großzügig, bleibt aber
  // in 1:1 / 16:9 innerhalb der Höhe.
  const radar = isVertical ? 700 : 540;
  const center = radar / 2;
  const rOuter = radar * 0.46;
  const rInner = radar * 0.3;

  const chipBox = isVertical ? 100 : 82;
  const chipIcon = Math.round(chipBox * 0.5);
  const chipLabelPx = isVertical ? sm * 0.8 : sm * 0.66;

  const eyePx = isVertical ? sm * 0.56 : sm * 0.58;
  const headPx = isVertical ? lg * 0.84 : lg * 0.6;
  const subPx = isVertical ? md * 0.6 : md * 0.4;
  const counterPx = isVertical ? sm * 0.64 : sm * 0.56;

  // Ids — IMMER mit clipPrefix, damit mehrere Clips nicht kollidieren.
  const wrapId = `${clipPrefix}-wrap`;
  const eyeId = `${clipPrefix}-eye`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const radarId = `${clipPrefix}-radar`;
  const sweepId = `${clipPrefix}-sweep`;
  const dotId = `${clipPrefix}-dot`;
  const counterId = `${clipPrefix}-counter`;
  const pinIds = parsed.map((_, i) => `${clipPrefix}-pin-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  // Pins organisch verteilen: gleichmäßiger Grundwinkel + leichter Jitter,
  // abwechselnd auf zwei Radien — wirkt natürlicher als ein perfekter Ring.
  const n = Math.max(parsed.length, 1);
  const placed = parsed.map((it, i) => {
    const jitter = i % 2 === 0 ? -11 : 13;
    const angleDeg = -90 + (360 / n) * i + jitter;
    const rad = (angleDeg * Math.PI) / 180;
    const radius = i % 2 === 0 ? rOuter : rInner;
    const cx = center + Math.cos(rad) * radius;
    const cy = center + Math.sin(rad) * radius;
    return { ...it, cx, cy };
  });

  function pin(i: number, p: (typeof placed)[number]): string {
    return `<div id="${pinIds[i]}" style="position:absolute;left:${p.cx.toFixed(1)}px;top:${p.cy.toFixed(1)}px;transform:translate(-50%,-50%);display:flex;flex-direction:column;align-items:center;gap:8px;will-change:transform,opacity;z-index:6;">
      <div style="width:${chipBox}px;height:${chipBox}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 32% 28%, ${withAlpha(accent, 0.55)} 0%, ${withAlpha(themeColors.bg, 0.92)} 78%);border:1.5px solid ${withAlpha(accent, 0.7)};box-shadow:0 0 26px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.18)};color:${themeColors.fg};">
        ${lucideIcon(p.icon, chipIcon, themeColors.fg, 2.2)}
      </div>
      ${p.label ? `<span style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${chipLabelPx}px;letter-spacing:0.2px;color:${withAlpha(themeColors.fg, 0.86)};white-space:nowrap;text-shadow:0 2px 10px ${withAlpha(themeColors.bg, 0.8)};">${escapeHtml(p.label)}</span>` : ""}
    </div>`;
  }

  function ring(scale: number, opacity: number): string {
    const size = (radar * scale).toFixed(1);
    return `<div style="position:absolute;left:50%;top:50%;width:${size}px;height:${size}px;transform:translate(-50%,-50%);border-radius:9999px;border:1.5px solid ${withAlpha(accent, opacity)};"></div>`;
  }

  const gridLine = (rot: number) =>
    `<div style="position:absolute;left:50%;top:50%;width:${radar}px;height:1px;transform:translate(-50%,-50%) rotate(${rot}deg);background:${withAlpha(accent, 0.14)};"></div>`;

  const pinsHtml = placed.map((p, i) => pin(i, p)).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
    <style>
      @keyframes ${clipPrefix}-spin { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }
      @keyframes ${clipPrefix}-pulse { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity: 1; } 50% { transform: translate(-50%,-50%) scale(1.55); opacity: 0.45; } }
      #${sweepId} { animation: ${clipPrefix}-spin 3.6s linear infinite; }
      #${dotId} { animation: ${clipPrefix}-pulse 1.8s ease-in-out infinite; }
    </style>

    <div style="position:absolute;left:50%;top:${isVertical ? 140 : 92}px;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 12 : 10}px;text-align:center;width:100%;max-width:${isVertical ? 960 : 1200}px;padding:0 ${isVertical ? 60 : 90}px;box-sizing:border-box;">
      <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};display:inline-flex;align-items:center;gap:14px;"><span style="width:30px;height:2px;background:${accent};"></span><span>${escapeHtml(eyebrow)}</span><span style="width:30px;height:2px;background:${accent};"></span></div>
      <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.04;letter-spacing:-1.4px;color:${themeColors.fg};text-wrap:balance;text-shadow:0 0 50px ${withAlpha(accent, 0.32)};">${escapeHtml(headline)}</div>
    </div>

    <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
      <div id="${radarId}" style="position:relative;width:${radar}px;height:${radar}px;border-radius:9999px;background:radial-gradient(circle at 50% 50%, ${withAlpha(accent, 0.16)} 0%, ${withAlpha(accent, 0.05)} 46%, ${withAlpha(themeColors.bg, 0)} 72%);will-change:transform,opacity;">
        ${ring(0.92, 0.26)}
        ${ring(0.64, 0.2)}
        ${ring(0.36, 0.16)}
        ${gridLine(0)}
        ${gridLine(90)}
        ${gridLine(45)}
        ${gridLine(135)}
        <div id="${sweepId}" style="position:absolute;left:50%;top:50%;width:${radar}px;height:${radar}px;transform:translate(-50%,-50%);border-radius:9999px;background:conic-gradient(from 0deg, ${withAlpha(accent, 0)} 0deg, ${withAlpha(accent, 0)} 290deg, ${withAlpha(accent, 0.32)} 352deg, ${withAlpha(accent, 0.7)} 360deg);pointer-events:none;z-index:4;">
          <div style="position:absolute;left:50%;top:0;width:2px;height:50%;transform:translateX(-50%);background:linear-gradient(180deg, ${withAlpha(accent, 0)} 0%, ${accent} 100%);transform-origin:50% 100%;box-shadow:0 0 10px ${withAlpha(accent, 0.7)};"></div>
        </div>
        <div id="${dotId}" style="position:absolute;left:50%;top:50%;width:${isVertical ? 18 : 15}px;height:${isVertical ? 18 : 15}px;border-radius:9999px;background:${accent};box-shadow:0 0 22px ${accent};z-index:5;"></div>
        <div id="${counterId}" style="position:absolute;left:50%;top:calc(50% + ${isVertical ? 36 : 30}px);transform:translateX(-50%);font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${counterPx}px;letter-spacing:1px;color:${withAlpha(themeColors.fg, 0.78)};white-space:nowrap;z-index:5;text-shadow:0 2px 12px ${withAlpha(themeColors.bg, 0.85)};">${placed.length} in der Nähe</div>
        ${pinsHtml}
      </div>
    </div>

    <div style="position:absolute;left:50%;bottom:${isVertical ? 150 : 92}px;transform:translateX(-50%);width:100%;max-width:${isVertical ? 860 : 1000}px;padding:0 ${isVertical ? 60 : 90}px;box-sizing:border-box;text-align:center;">
      <div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subPx}px;letter-spacing:-0.2px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;text-wrap:balance;">${escapeHtml(subtext)}</div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${eyeId}`, { opacity: 0, y: -8 }),
    gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }),
    gsapSetLine(`#${subId}`, { opacity: 0, y: 16 }),
    gsapSetLine(`#${radarId}`, { opacity: 0, scale: 0.82, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${counterId}`, { opacity: 0 }),
    ...pinIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0, transformOrigin: "50% 50%" })),
  ].join("\n");

  const tweens: string[] = [];
  tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.1));
  tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }, 0.2));
  tweens.push(t.to(`#${radarId}`, { opacity: 1, scale: 1, duration: 0.6, ease: "power3.out" }, 0.35));

  // Pins werden „vom Sweep aufgedeckt": gestaffeltes Pop-in über die ersten ~2.5s.
  const pinStart = 0.7;
  const pinWindow = Math.min(2.5, Math.max(1.0, clipDurationSec * 0.55));
  const step = pinWindow / Math.max(placed.length, 1);
  placed.forEach((_, i) => {
    tweens.push(t.to(`#${pinIds[i]}`, { opacity: 1, scale: 1, duration: 0.5, ease: "back.out(1.8)" }, pinStart + i * step));
  });

  tweens.push(t.to(`#${counterId}`, { opacity: 1, duration: 0.45, ease: "power2.out" }, pinStart + step * placed.length));
  tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, Math.min(pinStart + 0.4, clipDurationSec * 0.5)));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
